/**
 * The one place that knows the HTTP contract. Endpoint paths are listed in
 * `PATHS` so a rename on the backend is a one-line change here (the backend routers
 * — api/fields.py, api/runs.py, api/ui.py, api/tiles.py — are the other half of this
 * contract; ui/README.md documents it, and a backend test asserts that every path
 * below is actually served).
 *
 * Three rules that the rest of the UI depends on:
 *   * every failure becomes an ApiError carrying the server's machine-readable `reason`
 *     next to a readable message — never a silently swallowed promise, and never the
 *     literal `{}` that came out of stringifying an empty `detail` object;
 *   * a run is polled, and progress comes from the server only. We never invent a
 *     percentage: an honest "waiting for the server" is better than a fake bar;
 *   * a run is read with `include_payload=true`. The payload is the evidence — strip,
 *     rejected dates, indices — and it lives at the TOP level of the response, not
 *     under `result`.
 */

import * as session from "./session";
import type {
  AgreementMatrixCell,
  AgreementSummary,
  AuthUser,
  BatchRunStart,
  ErrorReason,
  Field,
  FieldMarker,
  FieldMarkerInput,
  FieldMarkerList,
  FieldMarkerPatch,
  FieldPatch,
  FieldPhoto,
  FieldPhotoList,
  FieldPhotoPatch,
  FieldUse,
  FieldUseStart,
  GeoJsonPolygon,
  GroundTruth,
  GroundTruthInput,
  PreviewDate,
  PreviewTiles,
  ProgressEvent,
  Run,
  RunFlags,
  RunStart,
  SatelliteDates,
  SatelliteTiles,
  ServerFlags,
  TokenResponse,
} from "./types";

/**
 * Where the API lives. EMPTY MEANS SAME ORIGIN — that is the production build: behind
 * Caddy the UI and the API share one host (docs/DEPLOY.md §2), so no hostname is baked
 * into the image and the site can move without a rebuild. A full URL is for local dev,
 * where the UI is on 4200/8090 and the API on 8100.
 */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? "http://localhost:8100").replace(
  /\/+$/,
  "",
);

/** What to name in a message to the user: with an empty base that is the site itself. */
export const apiOrigin = (): string => API_BASE || window.location.origin;

export const PATHS = {
  token: "/api/token",
  logout: "/api/logout",
  me: "/api/me",
  flags: "/api/flags",
  fields: "/api/fields",
  field: (id: number) => `/api/fields/${id}`,
  fieldRuns: (id: number) => `/api/fields/${id}/runs`,
  fieldUse: (id: number) => `/api/fields/${id}/field-use`,
  fieldDates: (id: number) => `/api/fields/${id}/dates`,
  fieldPreview: (id: number) => `/api/fields/${id}/preview`,
  fieldGroundTruth: (id: number) => `/api/fields/${id}/ground-truth`,
  fieldMarkers: (id: number) => `/api/fields/${id}/markers`,
  fieldMarker: (id: number, markerId: number) => `/api/fields/${id}/markers/${markerId}`,
  fieldPhotos: (id: number) => `/api/fields/${id}/photos`,
  fieldPhoto: (id: number, photoId: number) => `/api/fields/${id}/photos/${photoId}`,
  fieldPhotoFile: (id: number, photoId: number) => `/api/fields/${id}/photos/${photoId}/file`,
  fieldPhotoThumb: (id: number, photoId: number) => `/api/fields/${id}/photos/${photoId}/thumb`,
  run: (id: number) => `/api/runs/${id}`,
  runsBatch: "/api/runs/batch",
  agreement: "/api/agreement",
  agreementMatrix: "/api/agreement/matrix",
  // Imagery over a box: no field id in the path, which is the whole point (§2 of
  // docs/MAP_IMAGERY_RESEARCH.md). Still authenticated like everything else.
  satelliteDates: "/api/satellite/dates",
  satelliteTiles: (date: string) => `/api/satellite/tiles/${date}`,
} as const;

export class ApiError extends Error {
  readonly status: number;
  readonly path: string;
  /** api/errors.py ErrorReason, when the body carried one. */
  readonly reason: ErrorReason | null;
  /**
   * The machine-readable body of the refusal (api/errors.py ErrorResponse `detail`), when
   * there was one.
   *
   * IT IS NOT A SECOND MESSAGE. Nothing here is ever printed as prose — the sentence on
   * screen is always ours, chosen by `reason`. What travels here are NUMBERS that only the
   * server knows, and one of them earned this field: `too_many_photos` states the ceiling it
   * just applied in `detail.limit`, and a sentence about a ceiling has to quote the ceiling
   * that actually stopped you — not the one the client read from a list some time before.
   */
  readonly detail: Record<string, unknown> | null;

  constructor(
    message: string,
    status: number,
    path: string,
    reason: ErrorReason | null = null,
    options?: ErrorOptions & { detail?: Record<string, unknown> | null },
  ) {
    super(message, options);
    this.name = "ApiError";
    this.status = status;
    this.path = path;
    this.reason = reason;
    this.detail = options?.detail ?? null;
  }

  /** One number out of `detail`, or null. Never throws: a malformed body must not crash. */
  detailNumber(key: string): number | null {
    const value = this.detail?.[key];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }

  /** 404 on an optional endpoint means "backend does not offer this yet". */
  get isMissingEndpoint(): boolean {
    return this.status === 404;
  }

  /** No session, or one the server no longer accepts. The app must show the login screen. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** `application/x-www-form-urlencoded`, which is what OAuth2's token endpoint takes. */
  form?: Record<string, string>;
  /**
   * `multipart/form-data` — a file upload, and the one body whose Content-Type we must NOT
   * set: the header carries a boundary the browser generates with the body, and writing the
   * type by hand produces a boundary-less header the server cannot parse.
   */
  multipart?: FormData;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Login and registration are the two calls a 401 must NOT log the user out of. */
  anonymous?: boolean;
}

/**
 * `new URL(base + path)` alone throws on a relative base — `new URL("/api/fields")` has
 * nothing to resolve against. The page origin is that fallback; an absolute base still
 * wins over it, so local dev against port 8100 is unchanged.
 */
export const buildUrl = (
  path: string,
  query?: RequestOptions["query"],
  base: string = API_BASE,
): string => {
  const url = new URL(base + path, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== null && value !== undefined && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
};

interface ErrorBody {
  reason?: unknown;
  message?: unknown;
  detail?: unknown;
}

/**
 * The server's error shape is `{reason, message, detail}` (api/errors.py ErrorResponse) and
 * `detail` is `{}` on most failures. `detail ?? message` therefore picked an empty object
 * that stringified to the literal "{}" the user used to see — the message and the reason
 * were both thrown away. `message` wins; `detail` is only a fallback for a proxy or a
 * FastAPI validation body.
 */
interface ParsedError {
  message: string;
  reason: ErrorReason | null;
  detail: Record<string, unknown> | null;
}

const parseError = async (response: Response): Promise<ParsedError> => {
  const text = await response.text().catch(() => "");
  if (!text) return { message: `HTTP ${response.status}`, reason: null, detail: null };
  try {
    const parsed = JSON.parse(text) as ErrorBody;
    const reason = typeof parsed.reason === "string" ? (parsed.reason as ErrorReason) : null;
    // The structured `detail` travels on regardless of which branch supplies the message:
    // it carries numbers, and the message carries words.
    const detail =
      parsed.detail && typeof parsed.detail === "object" && !Array.isArray(parsed.detail)
        ? (parsed.detail as Record<string, unknown>)
        : null;
    if (typeof parsed.message === "string" && parsed.message) {
      return { message: parsed.message, reason, detail };
    }
    if (typeof parsed.detail === "string" && parsed.detail) {
      return { message: parsed.detail, reason, detail: null };
    }
    if (detail && Object.keys(detail).length > 0) {
      return { message: JSON.stringify(detail), reason, detail };
    }
    if (reason) return { message: reason, reason, detail };
  } catch {
    // Not JSON: a proxy page or an nginx error lands here.
  }
  return { message: text.slice(0, 400), reason: null, detail: null };
};

const buildHeaders = (options: RequestOptions): Record<string, string> => {
  const headers: Record<string, string> = { Accept: "application/json" };
  // `multipart` deliberately sets nothing: see the note on the option.
  if (options.form !== undefined) headers["Content-Type"] = "application/x-www-form-urlencoded";
  else if (options.multipart === undefined && options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (!options.anonymous) Object.assign(headers, session.authHeaders());
  return headers;
};

const buildBody = (options: RequestOptions): BodyInit | undefined => {
  if (options.multipart !== undefined) return options.multipart;
  if (options.form !== undefined) return new URLSearchParams(options.form).toString();
  if (options.body !== undefined) return JSON.stringify(options.body);
  return undefined;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", query, signal } = options;
  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers: buildHeaders(options),
      body: buildBody(options),
      signal,
    });
  } catch (cause) {
    // The browser's own words ("Failed to fetch") and the API address are debugging
    // material, not an answer: they travel on `cause`, never in the message on screen.
    throw new ApiError(
      "Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.",
      0,
      path,
      null,
      {
        cause,
      },
    );
  }
  if (!response.ok) {
    const { message, reason, detail } = await parseError(response);
    // One place for the whole app: a 401 from any of a dozen background polls drops the
    // token, and the session subscriber in App.tsx turns that into the login screen. Login
    // and registration are excluded — a wrong password there is an answer, not an expiry.
    if (response.status === 401 && !options.anonymous) session.clearToken();
    throw new ApiError(message, response.status, path, reason, { detail });
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// --- auth -----------------------------------------------------------------
//
// Bearer token, HS256, kept in localStorage (api/session.ts). Logging in is the whole of
// it: accounts are created by the owner from a shell, so there is nothing else to call.

/**
 * Log in and store the token. `username` is the OAuth2 form field name that
 * `OAuth2PasswordBearer` expects on the server; it carries our login, and no email is
 * involved anywhere in this product.
 */
export async function login(loginName: string, password: string): Promise<AuthUser> {
  const issued = await request<TokenResponse>(PATHS.token, {
    method: "POST",
    form: { username: loginName, password, grant_type: "password" },
    anonymous: true,
  });
  session.setToken(issued.access_token);
  return issued.user;
}

export const getMe = (signal?: AbortSignal): Promise<AuthUser> => request(PATHS.me, { signal });

/**
 * Discard the session. The token is signed, not stored, so dropping it locally IS the
 * logout; the request only tells the server, and a failure must not leave the user stuck on
 * a screen he asked to leave.
 */
export async function logout(): Promise<void> {
  try {
    await request(PATHS.logout, { method: "POST" });
  } catch {
    // Offline, expired, already invalid — the local clear below is what matters.
  }
  session.clearToken();
}

/** Some endpoints return `[...]`, some `{items: [...]}`. Accept both, once, here. */
const asList = <T>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === "object") {
    for (const key of [
      "items",
      "rows",
      "results",
      "fields",
      "runs",
      "dates",
      "observations",
      "markers",
    ]) {
      const candidate = (value as Record<string, unknown>)[key];
      if (Array.isArray(candidate)) return candidate as T[];
    }
  }
  return [];
};

// --- health and flags -----------------------------------------------------

export const getServerFlags = (signal?: AbortSignal): Promise<ServerFlags> =>
  request(PATHS.flags, { signal });

// --- fields ---------------------------------------------------------------

/**
 * The roster, most urgent first (the server sorts by priority). Archived fields are out
 * unless asked for; asking returns BOTH, each row carrying `archived_at`, so one request
 * builds the list with its archive underneath.
 */
export const listFields = async (
  options: { includeArchived?: boolean; signal?: AbortSignal } = {},
): Promise<Field[]> => {
  const path = options.includeArchived ? `${PATHS.fields}?include_archived=true` : PATHS.fields;
  return asList<Field>(await request(path, { signal: options.signal }));
};

export const createField = (input: {
  name: string;
  geometry: GeoJsonPolygon;
  source?: string;
}): Promise<Field> => request(PATHS.fields, { method: "POST", body: input });

/**
 * Rename, re-prioritise, annotate or (un)archive. ONE endpoint for all four: they are the
 * same act ("change a property of a field I own") and they need the same 404 for a field
 * that is not yours — a second write path is a second place to forget it.
 */
export const updateField = (id: number, patch: FieldPatch): Promise<Field> =>
  request(PATHS.field(id), { method: "PATCH", body: patch });

export const deleteField = (id: number): Promise<void> =>
  request(PATHS.field(id), { method: "DELETE" });

// --- markers on a field ---------------------------------------------------
//
// Points somebody put down by hand. Scoped to the field in the PATH, like ground truth and
// the watch, so "not your field" is one 404 and not a second ownership check to forget.
// The two refusals that have their own wording — the count and the distance — arrive as
// `reason` on the ApiError; nothing here parses a message.

/**
 * The pins AND the cap, kept together because the cap is what shuts the button.
 *
 * The list is the only place the server states its own ceiling, and the whole point of it
 * travelling in the body is that the twenty-first marker is refused BEFORE the click rather
 * than by a 409 after it. Unwrapping to a bare array here would throw that away, so this one
 * list endpoint does not go through `asList`.
 */
export const listMarkers = async (
  fieldId: number,
  signal?: AbortSignal,
): Promise<FieldMarkerList> => {
  const body = await request<Partial<FieldMarkerList>>(PATHS.fieldMarkers(fieldId), { signal });
  return {
    field_id: fieldId,
    markers: asList<FieldMarker>(body),
    count: body?.count ?? 0,
    // A missing cap must not read as "no room left": the ceiling is the server's, and until
    // it says otherwise the button stays open and the 409 remains the backstop.
    limit: body?.limit ?? Number.POSITIVE_INFINITY,
  };
};

export const createMarker = (fieldId: number, input: FieldMarkerInput): Promise<FieldMarker> =>
  request(PATHS.fieldMarkers(fieldId), { method: "POST", body: input });

/** Слова, дата, место или их сочетание — но никогда одна координата: тип это запрещает. */
export const updateMarker = (
  fieldId: number,
  markerId: number,
  patch: FieldMarkerPatch,
): Promise<FieldMarker> =>
  request(PATHS.fieldMarker(fieldId, markerId), { method: "PATCH", body: patch });

export const deleteMarker = (
  fieldId: number,
  markerId: number,
): Promise<{ deleted: boolean; marker_id: number }> =>
  request(PATHS.fieldMarker(fieldId, markerId), { method: "DELETE" });

// --- photographs of a field -----------------------------------------------
//
// Scoped to the field in the PATH, exactly like markers and ground truth: a photo id that
// belongs to somebody else's field answers the same 404 as one that never existed, and that
// matters more here than anywhere else because these ids travel in image URLs.
//
// THE IMAGE IS NOT AN `<img src>`. Both byte endpoints want the bearer token, and an `<img>`
// cannot carry one — so the file is fetched like every other request and turned into a blob
// URL. That is the reason `photoObjectUrl` exists and the reason its caller owns a revoke.

/**
 * The photos AND the cap, kept together because the cap is what shuts the button.
 *
 * Same shape and same reasoning as `listMarkers`: the ceiling travels in the body so the
 * thirty-first photo is refused BEFORE the click rather than by a 409 after it, which is why
 * this list endpoint does not unwrap to a bare array.
 */
export const listPhotos = async (
  fieldId: number,
  signal?: AbortSignal,
): Promise<FieldPhotoList> => {
  const body = await request<Partial<FieldPhotoList>>(PATHS.fieldPhotos(fieldId), { signal });
  return {
    field_id: fieldId,
    photos: Array.isArray(body?.photos) ? body.photos : [],
    count: body?.count ?? 0,
    // A missing cap must not read as "no room left": until the server states one the button
    // stays open and the 409 remains the backstop. То же и про потолок заметки.
    limit: body?.limit ?? Number.POSITIVE_INFINITY,
    note_limit: body?.note_limit ?? Number.POSITIVE_INFINITY,
  };
};

/**
 * Загрузить один файл. Формы перед загрузкой нет: `taken_on` сервер берёт из EXIF
 * (DateTimeOriginal), а подписи у фотографии больше не существует — комментарий принадлежит
 * заметке, и `comment` из этого запроса ушёл вместе с моделью (notes-v2).
 *
 * ДВА `marker_id`, И ОНИ ОТВЕЧАЮТ НА РАЗНЫЕ ВОПРОСЫ.
 *
 * `marker_id` — «положи в ЭТУ заметку», и сервер не смотрит на координаты вовсе. Его шлют из
 * карточки и из попапа, где человек уже выбрал, куда кладёт.
 *
 * `fallback_marker_id` — «если места в файле не окажется, положи ВОТ СЮДА», и сервер читает
 * его ровно в той ветке. Это цепочка одной пачки: первый безместный файл заводит заметку
 * поездки, её id приезжает в ответе, и следующие файлы той же пачки несут его здесь — иначе
 * двадцать кадров без GPS дали бы двадцать заметок вместо одной поездки.
 */
export const uploadPhoto = (
  fieldId: number,
  file: File,
  input: { taken_on?: string; marker_id?: number; fallback_marker_id?: number } = {},
  signal?: AbortSignal,
): Promise<FieldPhoto> => {
  const form = new FormData();
  form.append("file", file, file.name);
  if (input.taken_on !== undefined) form.append("taken_on", input.taken_on);
  // Numbers go into a multipart form as their decimal text; there is no other encoding.
  if (input.marker_id !== undefined) form.append("marker_id", String(input.marker_id));
  if (input.fallback_marker_id !== undefined) {
    form.append("fallback_marker_id", String(input.fallback_marker_id));
  }
  return request(PATHS.fieldPhotos(fieldId), { method: "POST", multipart: form, signal });
};

export const updatePhoto = (
  fieldId: number,
  photoId: number,
  patch: FieldPhotoPatch,
): Promise<FieldPhoto> =>
  request(PATHS.fieldPhoto(fieldId, photoId), { method: "PATCH", body: patch });

export const deletePhoto = (
  fieldId: number,
  photoId: number,
): Promise<{ deleted: boolean; photo_id: number }> =>
  request(PATHS.fieldPhoto(fieldId, photoId), { method: "DELETE" });

/**
 * Fetch the bytes and hand back a blob URL for them.
 *
 * The URL belongs to the CALLER and outlives this function: every one of them holds a decoded
 * image in memory until `URL.revokeObjectURL` is called on it, so a component that asks for
 * thirty thumbnails and forgets them leaks thirty images for the life of the document. Every
 * caller here keeps its own map and revokes on unmount.
 *
 * A failure throws an ApiError like any other request rather than resolving to a broken
 * image: "the server refused" and "the picture is corrupt" are different sentences.
 */
export const photoObjectUrl = async (
  fieldId: number,
  photoId: number,
  variant: "file" | "thumb" = "thumb",
  signal?: AbortSignal,
): Promise<string> => {
  const path =
    variant === "file"
      ? PATHS.fieldPhotoFile(fieldId, photoId)
      : PATHS.fieldPhotoThumb(fieldId, photoId);
  let response: Response;
  try {
    response = await fetch(buildUrl(path), { headers: session.authHeaders(), signal });
  } catch (cause) {
    throw new ApiError(
      "Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.",
      0,
      path,
      null,
      {
        cause,
      },
    );
  }
  if (!response.ok) {
    const { message, reason, detail } = await parseError(response);
    if (response.status === 401) session.clearToken();
    throw new ApiError(message, response.status, path, reason, { detail });
  }
  return URL.createObjectURL(await response.blob());
};

// --- field use (the field type, cell 1) -----------------------------------

/**
 * The cached classification, or null when it has never been computed.
 *
 * The endpoint answers 200 with `computed: false` for an uncomputed field — that state is the
 * norm and must not look like an error. It used to answer 404, which turned every poll into a
 * browser console error. A real failure still throws: "not computed" and "server is down"
 * must not look the same.
 */
export const getFieldUse = async (
  fieldId: number,
  signal?: AbortSignal,
): Promise<FieldUse | null> => {
  const status = await request<{ computed?: boolean; classification?: FieldUse | null }>(
    PATHS.fieldUse(fieldId),
    { signal },
  );
  return status.computed ? (status.classification ?? null) : null;
};

/** Queue the expensive classification. 202: there is no answer in the response. */
export const startFieldUse = (
  fieldId: number,
  input: { analysis_date?: string | null; history_years?: number | null } = {},
): Promise<FieldUseStart> => request(PATHS.fieldUse(fieldId), { method: "POST", body: input });

// --- runs -----------------------------------------------------------------

export const listRuns = async (fieldId: number, signal?: AbortSignal): Promise<Run[]> =>
  asList<Run>(await request(PATHS.fieldRuns(fieldId), { signal }));

/** Always with the payload: without it the evidence half of the interface is blank. */
export const getRun = (runId: number, signal?: AbortSignal): Promise<Run> =>
  request(PATHS.run(runId), { query: { include_payload: true }, signal });

export const startRun = (
  fieldId: number,
  input: { analysis_date?: string | null; flags?: Partial<RunFlags> } = {},
): Promise<RunStart> => request(PATHS.fieldRuns(fieldId), { method: "POST", body: input });

export const startBatch = (
  fieldIds: number[],
  input: { analysis_date?: string | null; flags?: Partial<RunFlags> } = {},
): Promise<BatchRunStart> =>
  request(PATHS.runsBatch, { method: "POST", body: { ...input, field_ids: fieldIds } });

const RUN_POLL_MS = 1200;

const progressOf = (run: Run): ProgressEvent[] => run.progress ?? run.payload?.progress ?? [];

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true },
    );
  });

const isFinished = (run: Run): boolean => run.status === "succeeded" || run.status === "failed";

/**
 * Follow a queued run to the end.
 *
 * The POST answers 202 with `{run_id, job_state}` and no verdict, so the run has to be
 * read at least once before anything can be shown. Reading `status` off the 202 body gave
 * `undefined`, the poll loop never ran, and the UI presented the acknowledgement as a
 * finished run with no verdict — "готово" forever.
 */
export async function followRun(
  runId: number,
  callbacks: { onProgress?: (events: ProgressEvent[]) => void; signal?: AbortSignal } = {},
): Promise<Run> {
  const { onProgress, signal } = callbacks;
  let run = await getRun(runId, signal);
  onProgress?.(progressOf(run));
  while (!isFinished(run)) {
    await sleep(RUN_POLL_MS, signal);
    run = await getRun(runId, signal);
    onProgress?.(progressOf(run));
  }
  return run;
}

export async function runField(
  fieldId: number,
  input: { analysis_date?: string | null; flags?: Partial<RunFlags> } = {},
  callbacks: { onProgress?: (events: ProgressEvent[]) => void; signal?: AbortSignal } = {},
): Promise<Run> {
  const started = await startRun(fieldId, input);
  return followRun(started.run_id, callbacks);
}

// --- ground truth ---------------------------------------------------------

export const listGroundTruth = async (
  fieldId: number,
  signal?: AbortSignal,
): Promise<GroundTruth[]> =>
  asList<GroundTruth>(await request(PATHS.fieldGroundTruth(fieldId), { signal }));

export const createGroundTruth = (fieldId: number, input: GroundTruthInput): Promise<GroundTruth> =>
  request(PATHS.fieldGroundTruth(fieldId), { method: "POST", body: input });

// СЛЕЖЕНИЕ УБРАНО ИЗ UI 2026-10-02 (решение владельца). Пять вызовов — статус, подписка,
// отписка, список подписок и состояние поля — удалены вместе с кнопкой: ручки `api/watch.py`
// на сервере живы, а клиент, который их не зовёт ни из одного места, был бы мёртвым кодом с
// видом рабочего. Вернуть фичу — значит вернуть и эти пять строк, по тому же контракту.

// --- agreement ------------------------------------------------------------

/**
 * The whole summary, not just the rows: matches, false positives, misses and the per-bucket
 * breakdown are computed by api/truth.py, which is also what feeds the CSV export. The UI
 * displays that arithmetic; it does not repeat it.
 */
export const getAgreement = (
  params: {
    field_id?: number | null;
    max_gap_days?: number | null;
    blind_only?: boolean;
  } = {},
  signal?: AbortSignal,
): Promise<AgreementSummary> => request(PATHS.agreement, { query: params, signal });

export const listAgreementMatrix = async (
  params: { field_id?: number | null; max_gap_days?: number | null } = {},
  signal?: AbortSignal,
): Promise<AgreementMatrixCell[]> =>
  asList<AgreementMatrixCell>(await request(PATHS.agreementMatrix, { query: params, signal }));

// --- satellite preview ----------------------------------------------------

export const listPreviewDates = async (
  fieldId: number,
  signal?: AbortSignal,
): Promise<PreviewDate[]> =>
  asList<PreviewDate>(await request(PATHS.fieldDates(fieldId), { signal }));

export const getPreviewTiles = (
  fieldId: number,
  date: string,
  signal?: AbortSignal,
): Promise<PreviewTiles> => request(PATHS.fieldPreview(fieldId), { query: { date }, signal });

// --- dated imagery over the viewport --------------------------------------
//
// The second of the three imagery layers: a picture of a date over a BOX, so it exists
// without a field, without a run and without anything having been computed. The server
// caches both for 6 h / 2 h and counts what it spends; the client-side guards that decide
// whether to call at all are in lib/viewport.ts.

export const listSatelliteDates = (bbox: string, signal?: AbortSignal): Promise<SatelliteDates> =>
  request(PATHS.satelliteDates, { query: { bbox }, signal });

export const getSatelliteTiles = (
  date: string,
  bbox: string,
  signal?: AbortSignal,
): Promise<SatelliteTiles> => request(PATHS.satelliteTiles(date), { query: { bbox }, signal });
